import {useColorMode} from '@vueuse/core'
import _ from 'lodash'
import {ref, computed} from 'vue'
import {getStandardMileageReport} from '@api'

// loadedReportId
// - 2024.01.09
// - A local cache that save `id` of loaded data
const loadedReportId = ref({})

// echartOption
const echartOption = ref(null)

// colorMode
const colorMode = useColorMode()

// reportMileagge
const reportMileage = ref(null)

// reportMileageDataPoints
const reportMileageDataPoints = ref(null)

// chartCurrentPointIndex
const chartCurrentPointIndex = ref(0)

// chartCurrentPointData
// - 2024.04.02
const chartCurrentPointData = computed({
  get () {
    return reportMileageDataPoints.value[chartCurrentPointIndex.value]
  },
})

// chartNextPointBtnShouldDisable
// - 2024.04.02
const chartNextPointBtnShouldDisable = computed({
  get () {
    return chartCurrentPointIndex.value === reportMileageDataPoints.value.length - 1
  },
})

// chartPrevPointBtnShouldDisable
// - 2024.04.02
const chartPrevPointBtnShouldDisable = computed({
  get () {
    return chartCurrentPointIndex.value === 0
  },
})

// reportMileageAlerts
// - 2024.04.08
const reportMileageAlerts = computed({
  get () {
    return reportMileage.value
			&& reportMileage.value.report
			&& reportMileage.value.report.exception_notification
			|| null
  },
})

// loadReportMileageChartOptions
// - 2024.01.09
// - Load report mileage chart options
function loadReportMileageChartOptions () {
  if (
    reportMileage.value
    && reportMileage.value.report
    && reportMileage.value.report.detail
  ) {
    reportMileageDataPoints.value = reportMileage.value.report.detail
  }

  if (reportMileageDataPoints.value) {
    reportMileageDataToEchartOption(reportMileageDataPoints.value)
  }
}

// loadReportMileage
// - 2024.04.01
async function loadReportMileage ({params}) {
  try {
    if (loadedReportId.value[params.module_id]) {
      reportMileage.value = loadedReportId.value[params.module_id]
      loadReportMileageChartOptions()

      return
    }

    const rb = await getStandardMileageReport({
      params,
    })

    // Cache data to local
    loadedReportId.value[params.module_id] = rb
    reportMileage.value = loadedReportId.value[params.module_id]
    loadReportMileageChartOptions()
  }
  catch (err) {
    alert(err.message || err || JSON.stringify(err))
  }
}

// generateSeriesOfLineChart
// - 2024.04.01
// - 2025.02.27
// - Fixed: Navigation buttons now work correctly when two points have identical dates
// - Date format changed from `YYYY-MM-DD` to `YYYY-MM-DD hh:mm:ss` ,
//   "2022-04-03" -> "2022-04-03 00:00:00"
function generateSeriesOfLineChart (data) {
  const lineSeries = data.map(({date, value}) => {
    return [new Date(date), _.toNumber(value.split('km')[0])]
  })

  return lineSeries
}

// generatePredictedSeries
// - 2024.04.01
function generatePredictedSeries (data) {
  const averageMilage = 20000
  const seriesOfLineChart = generateSeriesOfLineChart(data)
  const [firstPoint] = seriesOfLineChart
  const lastPoint = seriesOfLineChart[seriesOfLineChart.length - 1]

  // Calculate the predicted mileage for the second data point
  const monthOfSecondPoint = firstPoint[0].getMonth() + 1
  const predictedMileageOfSecondPoint = ((12 - monthOfSecondPoint + 1) / 12) * averageMilage

  // Generate the years range
  const firstYear = firstPoint[0].getFullYear()
  const lastYear = lastPoint[0].getFullYear()
  const years = Array.from({
    length: lastYear - firstYear + 2,
  }, (_index, i) => firstYear + i)

  const predictedSeries = years.map((year, index) => {
    if (index === 0) {
      return firstPoint
    }
    else if (index === 1) {
      const date = new Date(`${year}-01-01`)
      const mileage = predictedMileageOfSecondPoint

      return [date, mileage]
    }
    else {
      const date = new Date(`${year}-01-01`)
      const mileage = predictedMileageOfSecondPoint + averageMilage * (index - 1)

      return [date, mileage]
    }
  })

  return predictedSeries
}

// generateSeriesOfBarChart
// - 2024.04.01
function generateSeriesOfBarChart (data) {
  const head = _ .chain(data) .head() .get('date') .split('-') .head() .toNumber() .value()
  const last = (new Date()).getFullYear() + 2
  const duration = last - head

  const barSeries = _
    .chain(Array(duration))
    .fill(null)
    .map((_index, i) => ([
      head + i, 10000,
    ]))
    .value()

  return barSeries
}

// reportMileageDataToEchartOption
// - 2024.04.01
function reportMileageDataToEchartOption (data) {
  try {
    const head = _
      .chain(data)
      .map('date')
      .map((item) => {
        return _
          .chain(item)
          .split('-')
          .head()
          .toNumber()
          .value()
      })
      .min()
      .value()

    const last = (new Date()).getFullYear() + 2
    const min = new Date(head - 1, 11, 31)
    const max = new Date(last, 0, 2)

    const seriesOfLineChart = generateSeriesOfLineChart(data)
    const predictedSeries = generatePredictedSeries(data)
    const seriesOfBarChart = generateSeriesOfBarChart(data)
    const oneYear = 60 * 60 * 24 * 1000 * 365

    const option = {
      // backgourndColor
      backgroundColor: colorMode.value === 'light'
        ? '#ffffff'
        : '#1d1d1d',

      // grid reference
      grid: {
        show: false,
        top: 30,
        left: 50,
        right: 15,
        bottom: 35,
      },

      // x axis
      xAxis: [
        // x axis options of categroy
        {
          // type
          type: 'category',

          // axios pointer
          axisPointer: {
            show: false,
          },

          // axis line
          axisLine: {
            lineStyle: {
              color: colorMode.value === 'light' ? '#c1c1c1' : '#313131',
              width: 1,
            },
          },

          // axis label
          axisLabel: {
            color: colorMode.value === 'light' ? '#a1a1a1' : '#5A595B',
            fontSize: 11,
            align: 'center',
            margin: 10,
          },
        },

        // x axis options of time
        {
          // type
          type: 'time',

          // min
          min,

          // max
          max,

          // split area
          splitArea: {
            show: false,
            interval: oneYear,
          },

          axisTick: {
            show: false,
          },

          // interval: half a year
          interval: oneYear,

          // minInterval
          minInterval: oneYear,

          // maxInterval
          maxInterval: oneYear,

          // axisLabel
          axisLabel: {
            show: false,
            fontSize: 11,
            align: 'center',
            margin: 8,
            formatter: function (value, index) {
              return index % 2 !== 1
                ? _.toString((new Date(value)).getFullYear())
                : ''
            },
          },

          // axisLine
          axisLine: {
            show: false,
            lineStyle: {
              color: colorMode.value === 'light' ? '#a1a1a1' : '#313131',
            },
          },

          // 拖拽
          axisPointer: {
            show: true,
            // snap: true,
            type: 'line',
            // 初始值设定，从而决定 axisPointer 的初试位置
            // 默认初始值为 series 第一个元素的值
            value: seriesOfLineChart[0][0],

            handle: {
              show: true,
              color: '#ce261e',
              size: 27,
              margin: -155,
              shadowBlur: 0,
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              icon: colorMode.value === 'light'
                ? 'image://https://static.nuonuo.com.cn/zycmp/zyc-mileage-chart-arrow-20210825-01.png!/fw/1080'
                : 'image://https://static.nuonuo.com.cn/zycmp/standard-report-mileage-chart-handle-20250108-02.png',
            },

            // label
            label: {
              show: false,
              formatter: (params) => {
                const condition = params
                  && params.seriesData[0]
                  && params.seriesData[0].seriesIndex
                  && params.seriesData[0].seriesIndex === 0

                if (condition) {
                  chartCurrentPointIndex.value = params.seriesData[0].dataIndex
                }
                else {
                  // console.log(params)
                }
              },
            },
          },

          splitLine: {
            show: true,
            interval: oneYear,
            lineStyle: {
              color: colorMode.value === 'light' ? '#d8d8d8' : '#313131',
              type: 'dashed',
            },
          },
        },
      ],

      // y axis
      yAxis: [
        // y axis options of value
        {
          type: 'value',
          name: '(km)',
          nameLocation: 'end',
          nameGap: 10,
          min: 0,
          max: function (value) {
            return Math.ceil(value.max * 1.1 / 10000) * 10000
          },

          nameTextStyle: {
            fontWeight:'normal',
            color: colorMode.value === 'light' ? '#a1a1a1' : '#4d4d4d',
            fontSize: 11,
          },

          axisTick: {
            show: true,
            length: 2,
            lineStyle:{
              color: colorMode.value === 'light' ? '#c1c1c1' : '#313131',
            },
          },

          splitNumber: 10,

          axisLine: {
            show: true,
            lineStyle: {
              color: colorMode.value === 'light' ? '#c1c1c1' : '#313131',
            },
          },

          axisLabel: {
            show: true,
            color: colorMode.value === 'light' ? '#a1a1a1' : '#5A595B',
            fontSize: 10,
            margin: 5,
            // showMaxLabel: false,
          },

          splitLine: {
            show: true,
            lineStyle: {
              color: colorMode.value === 'light' ? '#d8d8d8' : '#313131',
              type: 'solid',
            },
          },
        },
      ],

      // seriese
      series: [

        // series #1 line chart
        {
          animation: false,
          data: seriesOfLineChart,
          type: 'line',
          selectedMode: true,
          symbol: 'circle',
          symbolSize: 6,
          legendHoverLink: true,
          xAxisIndex: 1,
          yAxisIndex: 0,
          itemStyle: {
            color: '#fefeb2',
            borderColor: '#3364a3',
          },

          lineStyle: {
            width: 2,
            color: colorMode.value === 'light' ? '#002247' : '#0070e0',
            type: 'solid',
          },

          emphasis: {
            scale: false,
            itemStyle: {
              color: '#fefeb2',
              borderColor: '#3364a3',
            },

            lineStyle: {
              width: 2,
              color: colorMode.value === 'light' ? '#002247' : '#0070e0',
              type: 'solid',
            },

          },

          smooth: false,
        },

        // series #2 bar chart
        {
          data: seriesOfBarChart,
          type: 'bar',
          barWidth: '100%',
          // use xAxis 0 index
          xAxisIndex: 0,
          yAxisIndex: 0,
          itemStyle: {
            color: 'transparent',
          },

          xAxis: {
            show:  false,
            axisLine: {
              show: false,
            },
          },

          yAxis: {
            show: false,
            axisLine: {
              show: false,
            },
          },

          emphasis: {
            itemStyle: {
              color: 'transparent',
            },
          },
        },

        // series #3 line chart
        {
          type: 'line',
          animation: false,
          silent: true,
          data: predictedSeries,
          xAxisIndex: 1,
          yAxisIndex: 0,
          symbol: 'none',
          label: {
            show: false,
          },

          emphasis: {
            disabled : true,
          },

          lineStyle: {
            type: 'dashed',
            width: 1,
            color: colorMode.value === 'light' ? '#0da8ff' : '#2CD056',
          },

          endLabel: {
            show: true,
            fontSize: 11,
            offset: [-142, 8],
            distance: 5,
            lineHeight: 15,
            color: colorMode.value === 'light' ? '#0da8ff' : '#2CD056',
            formatter: () => {
              return '主力车家用强度均线\n2万km/年'
            },
          },
        },
      ],

      // axis pointer
      axisPointer: {
        triggerOn: 'mousemove|click',
      },

      // tooltip
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: {
          axis: 'x',
          snap: false,
          lineStyle: {
            color: '#ff0000',
          },
        },

        triggerOn: 'mousemove|click',

        formatter: ([params]) => {
          if (params.seriesIndex && params.seriesIndex !== 0) {
            return
          }

          const {dataIndex} = params

          chartCurrentPointIndex.value = dataIndex

          return
        },
      },
    }

    echartOption.value = option
  }
  catch (err) {
    alert(err.message || err || JSON.stringify(err))
  }
}

// nextPointBtnDidPress
// - 2024.04.01
function nextPointInChartBtnDidPress (e, chartEl) {
  const totalLength = reportMileageDataPoints.value && reportMileageDataPoints.value.length || 0
  const targetIndex = chartCurrentPointIndex.value < totalLength
    ? chartCurrentPointIndex.value + 1
    : totalLength

  chartEl.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: targetIndex,
  })
}

// prevPointInChartBtnDidPress
// - 2024.04.02
function prevPointInChartBtnDidPress (e, chartEl) {
  const targetIndex = chartCurrentPointIndex.value > 0
    ? chartCurrentPointIndex.value - 1
    : 0

  chartEl.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: targetIndex,
  })
}

// useReportMileage
// - 2024.01.09
export function useReportMileage () {
  return {
    echartOption,
    loadReportMileage,
    nextPointInChartBtnDidPress,
    prevPointInChartBtnDidPress,
    reportMileageDataPoints,
    chartCurrentPointIndex,
    chartCurrentPointData,
    chartNextPointBtnShouldDisable,
    chartPrevPointBtnShouldDisable,
    reportMileage,
    reportMileageAlerts,
    loadReportMileageChartOptions,
  }
}
